import { useMemo } from 'react';

import { infiniteQueryOptions, useInfiniteQuery } from '@tanstack/react-query';

import { SortDirection } from '@cast/design-system';
import { toNumOrZero } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';

const WORKLOADS_RELIABILITY_METRICS_QUERY_KEY = 'workloads-reliability-metrics';

const getPaginatedWorkloadReliabilityMetricsQueryKey = ({
  clusterId,
  startTime,
  endTime,
  timezone,
  namespaces,
  workloadNames,
  workloadTypes,
  pageLimit,
  sortField,
  sortOrder,
  useListingPrices,
  includeIdleResourceCosts,
}: {
  clusterId: string;
  startTime: string;
  endTime: string;
  timezone: string;
  namespaces: string[] | undefined;
  workloadNames: string[] | undefined;
  workloadTypes: string[] | undefined;
  pageLimit: number;
  sortField: string | undefined;
  sortOrder: SortDirection | undefined;
  useListingPrices: boolean | undefined;
  includeIdleResourceCosts: boolean | undefined;
}) =>
  [
    WORKLOADS_RELIABILITY_METRICS_QUERY_KEY,
    clusterId,
    startTime,
    endTime,
    timezone,
    namespaces,
    workloadNames,
    workloadTypes,
    pageLimit,
    sortField,
    sortOrder,
    useListingPrices ?? false,
    includeIdleResourceCosts ?? false,
  ] as const;

type PaginatedWorkloadReliabilityMetricsQueryArgs = {
  clusterId: string;
  startTime: string;
  endTime: string;
  timezone: string;
  namespaces?: string[];
  workloadNames?: string[];
  workloadTypes?: string[];
  pageLimit?: number;
  sortField?: string;
  sortOrder?: SortDirection;
  useListingPrices?: boolean;
  includeIdleResourceCosts?: boolean;
  enabled?: boolean;
  refetchInterval?: number;
};

const getPaginatedWorkloadReliabilityMetricsQueryOptions = ({
  clusterId,
  startTime,
  endTime,
  timezone,
  namespaces,
  workloadNames,
  workloadTypes,
  pageLimit = 50,
  sortField,
  sortOrder,
  useListingPrices,
  includeIdleResourceCosts,
  enabled = true,
  refetchInterval,
}: PaginatedWorkloadReliabilityMetricsQueryArgs) =>
  infiniteQueryOptions({
    queryKey: getPaginatedWorkloadReliabilityMetricsQueryKey({
      clusterId,
      startTime,
      endTime,
      timezone,
      namespaces,
      workloadNames,
      workloadTypes,
      pageLimit,
      sortField,
      sortOrder,
      useListingPrices,
      includeIdleResourceCosts,
    }),
    queryFn: async ({ pageParam }) => {
      const { data } = await apiClient.costReport.getWorkloadReliabilityMetrics(
        {
          clusterId,
          startTime,
          endTime,
          timezone,
          workloadNames,
          workloadTypes,
          namespaces,
          pageLimit: String(pageLimit),
          pageCursor: pageParam,
          sortField,
          sortOrder,
          useListingPrices,
          includeIdleResourceCosts,
        }
      );

      const items =
        data.items
          ?.filter(
            (item) => item.workloadName && item.workloadType && item.namespace
          )
          .map((item) => ({
            ...item,
            id: `${item.workloadName}-${item.workloadType}-${item.namespace}`,
          })) ?? [];

      return {
        ...data,
        items,
      };
    },
    initialPageParam: '',
    getNextPageParam: ({ nextCursor }) =>
      nextCursor?.length ? nextCursor : undefined,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    refetchInterval,
    enabled,
  });

export const usePaginatedWorkloadReliabilityMetricsQuery = (
  props: PaginatedWorkloadReliabilityMetricsQueryArgs
) => {
  const {
    isLoading,
    data,
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetching,
  } = useInfiniteQuery(
    getPaginatedWorkloadReliabilityMetricsQueryOptions(props)
  );

  const workloads = useMemo(
    () => data?.pages.flatMap((page) => page.items ?? []) ?? [],
    [data]
  );

  const lastPage = data?.pages.at(-1);

  return {
    workloads,
    count: data ? toNumOrZero(lastPage?.count) : undefined,
    isLoading,
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetching,
  };
};
